import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Phone, TrendingUp, Users, RefreshCw, LogOut, Clock, Zap, ChevronDown, ChevronUp, ArrowLeft, AlertTriangle } from 'lucide-react';

const fmt = (s) => {
  if (!s && s !== 0) return '00:00:00';
  const n = parseInt(s);
  const h = Math.floor(n/3600).toString().padStart(2,'0');
  const m = Math.floor((n%3600)/60).toString().padStart(2,'0');
  const ss = (n%60).toString().padStart(2,'0');
  return `${h}:${m}:${ss}`;
};
const pct = (a, b) => b > 0 ? Math.round((a/b)*100) : 0;

const COR = {
  laranja:    '#F07020',
  laranjaEsc: '#C85A10',
  azul:       '#1E6DB5',
  ciano:      '#29B6D8',
  verde:      '#22C55E',
  amarelo:    '#F5A623',
  vermelho:   '#EF4444',
  bg:         '#0A0F1A',
  card:       '#0E1826',
  borda:      '#1A2740',
  texto:      '#F1F5F9',
  sub:        '#64748B',
};

const METAS_URA  = { qualificadas: 50,   indicacoes: 60,   vendas: 45 };
const METAS_DISC = { qualificadas: 14.5, indicacoes: 23.5, vendas: 34 };

export default function PainelSupervisor() {
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();
  const [periodo, setPeriodo] = useState('hoje');
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [atualizando, setAtualizando] = useState(false);
  const [hora, setHora] = useState(new Date().toLocaleTimeString('pt-BR'));
  const [expandido, setExpandido] = useState(null);
  const [ordenar, setOrdenar] = useState('ligacoes');
  const [dupContagem, setDupContagem] = useState(0);

  const carregar = useCallback(async (manual = false) => {
    if (manual) setAtualizando(true); else setLoading(true);
    try {
      const res = await api.get(`/metricas/equipe?periodo=${periodo}`);
      setDados(res.data);
      setHora(new Date().toLocaleTimeString('pt-BR'));
    } catch (err) { console.error(err); }
    finally { setLoading(false); setAtualizando(false); }
  }, [periodo]);

  const carregarContagem = useCallback(async () => {
    try {
      const res = await api.get('/registros/duplicidades/contagem');
      setDupContagem(res.data.total || 0);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    carregar();
    carregarContagem();
    const t = setInterval(() => { carregar(); carregarContagem(); }, 5*60*1000);
    return () => clearInterval(t);
  }, [carregar, carregarContagem]);

  const handleLogout = () => { logout(); navigate('/login'); };

  if (loading) return (
    <div style={{minHeight:'100vh',background:COR.bg,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column'}}>
      <div style={{width:40,height:40,border:`3px solid ${COR.borda}`,borderTop:`3px solid ${COR.laranja}`,borderRadius:'50%',animation:'spin 1s linear infinite'}}/>
      <p style={{color:COR.sub,marginTop:16,fontFamily:'sans-serif'}}>Carregando equipe...</p>
    </div>
  );

  const ops = dados?.operadores || [];
  const totais = dados?.totais || {};

  const totaisUra = ops.reduce((acc, op) => ({
    ligacoes:     acc.ligacoes     + op.ligacoes.ura,
    qualificadas: acc.qualificadas + op.ligacoes.qual_ura,
    indicacoes:   acc.indicacoes   + op.indicacoes.ura,
    vendas:       acc.vendas       + op.vendas.ura,
  }), { ligacoes:0, qualificadas:0, indicacoes:0, vendas:0 });

  const totaisDisc = ops.reduce((acc, op) => ({
    ligacoes:     acc.ligacoes     + op.ligacoes.discador,
    qualificadas: acc.qualificadas + op.ligacoes.qual_discador,
    indicacoes:   acc.indicacoes   + op.indicacoes.discador,
    vendas:       acc.vendas       + op.vendas.discador,
  }), { ligacoes:0, qualificadas:0, indicacoes:0, vendas:0 });

  const opsOrdenados = [...ops].sort((a, b) => {
    if (ordenar === 'ligacoes')    return b.ligacoes.total - a.ligacoes.total;
    if (ordenar === 'vendas')      return b.vendas.total - a.vendas.total;
    if (ordenar === 'indicacoes')  return b.indicacoes.total - a.indicacoes.total;
    if (ordenar === 'qualificadas')return b.ligacoes.qualificadas - a.ligacoes.qualificadas;
    return 0;
  });

  return (
    <div style={{minHeight:'100vh',background:COR.bg,padding:'20px 24px',fontFamily:'sans-serif'}}>
      <div style={{position:'fixed',top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${COR.laranja},${COR.ciano},${COR.azul})`,zIndex:100}}/>

      {/* HEADER */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20,paddingTop:8}}>
        <div style={{display:'flex',alignItems:'center',gap:16}}>
          <button style={{background:COR.card,border:`1px solid ${COR.borda}`,borderRadius:8,padding:'8px 12px',color:COR.sub,cursor:'pointer',display:'flex',alignItems:'center',gap:6,fontSize:12}}
            onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={14}/> Voltar
          </button>
          <div style={{width:1,height:40,background:COR.borda}}/>
          <div>
            <div style={{display:'flex',alignItems:'center',gap:6,color:COR.verde,fontSize:10,fontWeight:700,letterSpacing:1.5,marginBottom:3}}>
              <div style={{width:6,height:6,background:COR.verde,borderRadius:'50%'}}/> AO VIVO
            </div>
            <h1 style={{color:COR.texto,fontSize:20,fontWeight:700,margin:0}}>
              Equipe — {dados?.equipe || usuario?.agent_name}
            </h1>
            <p style={{color:COR.sub,fontSize:11,marginTop:2}}>Atualizado às {hora} · {ops.length} operadores</p>
          </div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          {usuario?.perfil === 'administrador' && (
            <button style={{display:'flex',alignItems:'center',gap:6,background:COR.card,border:`1px solid ${COR.azul}`,borderRadius:8,padding:'8px 14px',color:COR.ciano,cursor:'pointer',fontSize:12,fontWeight:600}}
              onClick={() => navigate('/usuarios')}>
              <Users size={14}/> Usuários
            </button>
          )}

          {/* BOTÃO DUPLICIDADES COM BADGE */}
          <button
            style={{
              display:'flex',alignItems:'center',gap:6,
              background: dupContagem > 0 ? COR.vermelho+'22' : COR.card,
              border:`1px solid ${dupContagem > 0 ? COR.vermelho+'66' : COR.borda}`,
              borderRadius:8,padding:'8px 14px',
              color: dupContagem > 0 ? COR.vermelho : COR.sub,
              cursor:'pointer',fontSize:12,fontWeight:600,
              position:'relative',
            }}
            onClick={() => navigate('/duplicidades')}>
            <AlertTriangle size={14}/>
            Duplicidades
            {dupContagem > 0 && (
              <span style={{
                background:COR.vermelho,color:'#fff',
                borderRadius:'50%',width:18,height:18,
                display:'flex',alignItems:'center',justifyContent:'center',
                fontSize:10,fontWeight:800,marginLeft:2,
              }}>
                {dupContagem > 9 ? '9+' : dupContagem}
              </span>
            )}
          </button>

          <button style={{background:COR.card,border:`1px solid ${COR.borda}`,borderRadius:8,padding:'8px 10px',color:COR.sub,cursor:'pointer'}}
            onClick={() => carregar(true)} disabled={atualizando}>
            <RefreshCw size={14} style={atualizando?{animation:'spin 1s linear infinite'}:{}}/>
          </button>
          <button style={{display:'flex',alignItems:'center',gap:6,background:COR.card,border:'1px solid #ef444455',borderRadius:8,padding:'8px 14px',color:'#ef4444',cursor:'pointer',fontSize:12}}
            onClick={handleLogout}>
            <LogOut size={14}/> Sair
          </button>
        </div>
      </div>

      {/* ALERTA DE DUPLICIDADES PENDENTES */}
      {dupContagem > 0 && (
        <div
          onClick={() => navigate('/duplicidades')}
          style={{
            display:'flex',alignItems:'center',gap:10,
            background:COR.vermelho+'15',border:`1px solid ${COR.vermelho+'44'}`,
            borderRadius:10,padding:'10px 16px',marginBottom:16,cursor:'pointer',
          }}>
          <AlertTriangle size={16} color={COR.vermelho}/>
          <span style={{color:COR.vermelho,fontSize:13,fontWeight:600}}>
            {dupContagem} solicitação{dupContagem > 1 ? 'ões' : ''} de duplicidade pendente{dupContagem > 1 ? 's' : ''} aguardando sua análise
          </span>
          <span style={{color:COR.vermelho,marginLeft:'auto',fontSize:12,textDecoration:'underline'}}>Ver agora →</span>
        </div>
      )}

      {/* FILTROS */}
      <div style={{display:'flex',gap:6,marginBottom:20}}>
        {['hoje','semana','mes'].map(f => (
          <button key={f}
            style={{background:periodo===f?`linear-gradient(135deg,${COR.laranja},${COR.laranjaEsc})`:COR.card,
              color:periodo===f?'#fff':COR.sub,
              border:`1px solid ${periodo===f?COR.laranja:COR.borda}`,
              borderRadius:8,padding:'7px 20px',cursor:'pointer',fontSize:13,fontWeight:500}}
            onClick={() => setPeriodo(f)}>
            {f.charAt(0).toUpperCase()+f.slice(1)}
          </button>
        ))}
      </div>

      {/* CARDS TOTAIS */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:14,marginBottom:20}}>
        <CardTotal titulo="Ligações"     valor={totais.ligacoes||0}     cor={COR.laranja} icon={<Phone size={14} color={COR.laranja}/>}/>
        <CardTotal titulo="Qualificadas" valor={totais.qualificadas||0} cor={COR.ciano}   icon={<Zap size={14} color={COR.ciano}/>}/>
        <CardTotal titulo="Indicações"   valor={totais.indicacoes||0}   cor={COR.amarelo} icon={<TrendingUp size={14} color={COR.amarelo}/>}/>
        <CardTotal titulo="Vendas"       valor={totais.vendas||0}       cor={COR.verde}   icon={<TrendingUp size={14} color={COR.verde}/>}/>
        <CardTotal titulo="Tempo Total"  valor={fmt(totais.tempo_total)}cor={COR.azul}    icon={<Clock size={14} color={COR.azul}/>} mono/>
      </div>

      {/* LAYOUT: tabela + funil */}
      <div style={{display:'flex',gap:20,alignItems:'flex-start'}}>

        {/* TABELA */}
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
            <span style={{color:COR.sub,fontSize:12}}>Ordenar por:</span>
            {['ligacoes','qualificadas','indicacoes','vendas'].map(k => (
              <button key={k}
                style={{background:ordenar===k?COR.azul+'33':COR.card,border:`1px solid ${ordenar===k?COR.azul:COR.borda}`,borderRadius:6,padding:'4px 12px',color:ordenar===k?COR.ciano:COR.sub,cursor:'pointer',fontSize:11,fontWeight:600}}
                onClick={() => setOrdenar(k)}>
                {k.charAt(0).toUpperCase()+k.slice(1)}
              </button>
            ))}
          </div>

          <div style={{background:COR.card,border:`1px solid ${COR.borda}`,borderRadius:12,overflow:'hidden'}}>
            <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 1fr 1fr 40px',gap:8,padding:'10px 16px',borderBottom:`1px solid ${COR.borda}`,background:'#080f1a'}}>
              {['Operador','Ligações','Qualif.','% Qual.','Indicações','Vendas','T. Fala',''].map((c,i) => (
                <span key={i} style={{color:COR.sub,fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:0.5}}>{c}</span>
              ))}
            </div>

            {opsOrdenados.length === 0 && (
              <div style={{padding:'32px',textAlign:'center',color:COR.sub,fontStyle:'italic'}}>
                Nenhum operador encontrado nesta equipe.
              </div>
            )}

            {opsOrdenados.map((op, idx) => {
              const pctQual = pct(op.ligacoes.qualificadas, op.ligacoes.total);
              const isExp = expandido === op.id;
              return (
                <div key={op.id}>
                  <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 1fr 1fr 40px',gap:8,padding:'12px 16px',borderBottom:`1px solid ${COR.borda}`,background:idx%2===0?'transparent':'#090e1a',cursor:'pointer'}}
                    onClick={() => setExpandido(isExp ? null : op.id)}>
                    <div>
                      <p style={{color:COR.texto,fontSize:13,fontWeight:600,margin:0}}>{op.nome}</p>
                      <p style={{color:COR.sub,fontSize:10,margin:'2px 0 0 0'}}>{op.agent_name}</p>
                    </div>
                    <NumCell valor={op.ligacoes.total} cor={COR.laranja}/>
                    <NumCell valor={op.ligacoes.qualificadas} cor={COR.ciano}/>
                    <PctCell valor={pctQual} meta={20}/>
                    <NumCell valor={op.indicacoes.total} cor={COR.amarelo}/>
                    <NumCell valor={op.vendas.total} cor={COR.verde}/>
                    <span style={{color:'#94a3b8',fontSize:12,fontFamily:'monospace',alignSelf:'center'}}>{fmt(op.ligacoes.tempo_total)}</span>
                    <span style={{color:COR.sub,alignSelf:'center',justifySelf:'center'}}>
                      {isExp ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                    </span>
                  </div>

                  {isExp && (
                    <div style={{background:'#080d18',borderBottom:`1px solid ${COR.borda}`,padding:'14px 20px'}}>
                      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
                        <MiniCard titulo="Ligações" cor={COR.laranja}
                          items={[['Total',op.ligacoes.total],['URA',op.ligacoes.ura],['Discador',op.ligacoes.discador],['Qualif. URA',op.ligacoes.qual_ura],['Qualif. Disc.',op.ligacoes.qual_discador]]}/>
                        <MiniCard titulo="Indicações" cor={COR.amarelo}
                          items={[['Total',op.indicacoes.total],['URA',op.indicacoes.ura],['Discador',op.indicacoes.discador],
                            ['% conv. URA',`${pct(op.indicacoes.ura,op.ligacoes.qual_ura)}%`],
                            ['% conv. Disc',`${pct(op.indicacoes.discador,op.ligacoes.qual_discador)}%`]]}/>
                        <MiniCard titulo="Vendas" cor={COR.verde}
                          items={[['Total',op.vendas.total],['URA',op.vendas.ura],['Discador',op.vendas.discador],
                            ['% conv. URA',`${pct(op.vendas.ura,op.indicacoes.ura)}%`],
                            ['% conv. Disc',`${pct(op.vendas.discador,op.indicacoes.discador)}%`]]}/>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* FUNIL DA EQUIPE */}
        <div style={{width:340,background:COR.card,border:`1px solid ${COR.borda}`,borderRadius:12,padding:'20px 18px',flexShrink:0}}>
          <div style={{borderBottom:`1px solid ${COR.borda}`,paddingBottom:12,marginBottom:16}}>
            <p style={{color:COR.texto,fontSize:14,fontWeight:700,margin:0,textAlign:'center'}}>Funil da Equipe</p>
            <p style={{color:COR.sub,fontSize:11,margin:'3px 0 0 0',textAlign:'center'}}>{periodo.charAt(0).toUpperCase()+periodo.slice(1)}</p>
          </div>

          <FunnelVisual titulo="URA" icone="↔"
            coresEtapas={[COR.laranja, COR.ciano, COR.amarelo, COR.verde]}
            etapas={[
              { label:'Atendidas',    valor: totaisUra.ligacoes,    meta: null,                   base: null },
              { label:'Qualificadas', valor: totaisUra.qualificadas,meta: METAS_URA.qualificadas,  base: totaisUra.ligacoes },
              { label:'Indicações',   valor: totaisUra.indicacoes,  meta: METAS_URA.indicacoes,    base: totaisUra.qualificadas },
              { label:'Vendas',       valor: totaisUra.vendas,      meta: METAS_URA.vendas,        base: totaisUra.indicacoes },
            ]}
          />
          <div style={{height:1,background:COR.borda,margin:'18px 0'}}/>
          <FunnelVisual titulo="Discador" icone="⚡"
            coresEtapas={[COR.azul, COR.ciano, COR.amarelo, COR.verde]}
            etapas={[
              { label:'Atendidas',    valor: totaisDisc.ligacoes,    meta: null,                    base: null },
              { label:'Qualificadas', valor: totaisDisc.qualificadas,meta: METAS_DISC.qualificadas,  base: totaisDisc.ligacoes },
              { label:'Indicações',   valor: totaisDisc.indicacoes,  meta: METAS_DISC.indicacoes,    base: totaisDisc.qualificadas },
              { label:'Vendas',       valor: totaisDisc.vendas,      meta: METAS_DISC.vendas,        base: totaisDisc.indicacoes },
            ]}
          />
        </div>
      </div>
    </div>
  );
}

function FunnelVisual({ titulo, icone, coresEtapas, etapas }) {
  const larguras = [100, 80, 62, 46];
  return (
    <div style={{width:'100%'}}>
      <p style={{fontSize:12,fontWeight:700,textAlign:'center',margin:'0 0 14px 0',letterSpacing:1.5,textTransform:'uppercase',color:coresEtapas[0]}}>
        {icone} {titulo}
      </p>
      <div style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
        {etapas.map((etapa, i) => {
          const convAtual = etapa.base > 0 ? Math.round((etapa.valor/etapa.base)*100) : null;
          const acimaMeta = etapa.meta !== null && convAtual !== null ? convAtual >= etapa.meta : null;
          const progresso = etapa.meta ? Math.min(((convAtual??0)/etapa.meta)*100, 100) : 0;
          const cor = coresEtapas[i];
          return (
            <div key={i} style={{width:'100%',display:'flex',flexDirection:'column',alignItems:'center'}}>
              {i > 0 && (
                <div style={{width:`${larguras[i]}%`,margin:'5px 0',padding:'5px 10px',background:'#080f1a',borderRadius:6,border:`1px solid ${acimaMeta?COR.verde+'44':'#ef444433'}`}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                    <div style={{display:'flex',alignItems:'center',gap:5}}>
                      <div style={{width:7,height:7,borderRadius:'50%',background:acimaMeta?COR.verde:'#ef4444',boxShadow:`0 0 8px ${acimaMeta?COR.verde:'#ef4444'}`}}/>
                      <span style={{color:acimaMeta?COR.verde:'#ef4444',fontSize:12,fontWeight:800}}>{convAtual??0}%</span>
                    </div>
                    <span style={{color:COR.sub,fontSize:11,fontWeight:600}}>meta {etapa.meta}%</span>
                  </div>
                  <div style={{width:'100%',height:3,background:COR.borda,borderRadius:2,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${progresso}%`,background:acimaMeta?`linear-gradient(90deg,${COR.verde},#4ade80)`:'linear-gradient(90deg,#ef4444,#f87171)',borderRadius:2}}/>
                  </div>
                </div>
              )}
              <div style={{width:`${larguras[i]}%`,background:`linear-gradient(135deg,${cor}ee,${cor}99)`,border:`1px solid ${cor}66`,borderRadius:i===0?'10px 10px 4px 4px':i===etapas.length-1?'4px 4px 10px 10px':'6px',padding:'11px 14px',display:'flex',justifyContent:'space-between',alignItems:'center',boxShadow:`0 4px 20px ${cor}28,inset 0 1px 0 rgba(255,255,255,0.15)`}}>
                <span style={{color:'rgba(255,255,255,0.9)',fontSize:11,fontWeight:600}}>{etapa.label}</span>
                <span style={{color:'#fff',fontSize:20,fontWeight:800,textShadow:'0 1px 6px rgba(0,0,0,0.4)'}}>{etapa.valor}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CardTotal({ titulo, valor, cor, icon, mono }) {
  return (
    <div style={{background:`linear-gradient(135deg,${cor}18,${cor}06)`,border:`1px solid ${cor}40`,borderRadius:12,padding:'12px 16px'}}>
      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>{icon}<span style={{color:cor,fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:0.5}}>{titulo}</span></div>
      <span style={{color:cor,fontSize:mono?18:32,fontWeight:800,fontFamily:mono?'monospace':'inherit'}}>{valor}</span>
    </div>
  );
}

function NumCell({ valor, cor }) {
  return <span style={{color:cor,fontSize:14,fontWeight:700,alignSelf:'center'}}>{valor}</span>;
}

function PctCell({ valor, meta }) {
  const ok = valor >= meta;
  return <span style={{color:ok?COR.verde:COR.vermelho,fontSize:13,fontWeight:700,alignSelf:'center'}}>{valor}%</span>;
}

function MiniCard({ titulo, cor, items }) {
  return (
    <div style={{background:COR.card,border:`1px solid ${cor}33`,borderRadius:10,padding:'10px 14px'}}>
      <p style={{color:cor,fontSize:11,fontWeight:700,margin:'0 0 8px 0',textTransform:'uppercase',letterSpacing:0.5}}>{titulo}</p>
      {items.map(([label, val]) => (
        <div key={label} style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
          <span style={{color:COR.sub,fontSize:11}}>{label}</span>
          <span style={{color:COR.texto,fontSize:12,fontWeight:600}}>{val}</span>
        </div>
      ))}
    </div>
  );
}
